<script setup lang="ts">
import { computed, ref } from 'vue';
import type {
  ApiResponseBase,
  Files,
  FilesOptions,
  GenericApiResponse,
  LeadFilesResponse,
} from '@apparatix/types/Leads';
import {
  useEndpoint,
  useOpportunities,
} from '@apparatix/composables';
import Column from 'primevue/column';
import ConfirmDialog from 'primevue/confirmdialog';
import DataTable from 'primevue/datatable';
import { useConfirm } from 'primevue/useconfirm';
import FileUpload, { FileUploadUploaderEvent } from 'primevue/fileupload';
import { Section } from '@apparatix/library/core';

interface FileUploaderProps {
  id: number;
  endpoint: string;
}

const props = defineProps<FileUploaderProps>();

const { uploadFile } = useOpportunities();

const data = ref<Files[]>([]);
const options = ref<FilesOptions>();

const {
  isLoading: isLoading, makeRequest: getFileRequest, setEndpointUrl: filesRequestEndpoint,
} = useEndpoint<ApiResponseBase<LeadFilesResponse>>({
  url: '',
  type: 'GET',
  successField: 'data',
  onSuccess: response => {
    data.value = response.data.files;
    options.value = response.data.options;
  },
  toast: { showOnSuccess: false },
});

filesRequestEndpoint(`/crm/${props.endpoint}/${props.id}/files`);

getFileRequest();
const filesDeleteRequestEndpoint = useEndpoint<GenericApiResponse>({
  url: `/crm/${props.endpoint}/${props.id}/files`,
  type: 'DELETE',
  successField: 'success',
  toast: { showOnSuccess: false },
});

const url = `/crm/${props.endpoint}/${props.id}/files`;

const confirm = useConfirm();

const onDeleteFile = (file: any) => {
  confirm.require({
    header: 'Confirm',
    group: 'removeFile',
    message: 'Are you sure you want to delete this file?',
    acceptLabel: 'Yes',
    rejectLabel: 'Cancel',

    accept: async () => {
      const payload = { data: { file: file }};
      await filesDeleteRequestEndpoint.makeRequest(payload);
      await getFileRequest();
    },
  });
};

const onUpload = async (id: number, event: FileUploadUploaderEvent) => {
  const files: File[] = Array.isArray(event.files) ? event.files : [event.files];
  await uploadFile(id, files, props.endpoint);
  await getFileRequest();
};

const fileSizeInMB = computed(() => options.value.fileSizeLimit / 1_000_000);

const assignRowClass = () => 'custom-row';

</script>
<template>
  <Section class="mb-3" :footer="false">
    <div class="grid grid-cols-1 grid-flow-row auto-rows-auto gap-y-2 gap-x-4 p-6">
      <!-- Intentionally using v-show for faster rendering -->
      <div v-show="data.length > 0">
        <DataTable
          :value="data"
          :row-class="assignRowClass"
          data-key="fileName"
          responsive-layout="scroll"
          :show-headers="false"
        >
          <Column field="fileName" class="text-[var(--text-link-primary)] truncate">
            <template #body="slotProps">
              <a :href="slotProps.data.url" download :title="'Download ' + slotProps.data.fileName + ' - Uploaded: ' + slotProps.data.date">
                {{ slotProps.data.fileName }}
              </a>
            </template>
          </Column>
          <Column style="width: 20px">
            <template #body="slotProps">
              <Button
                v-tooltip.left="'Delete File'"
                size="normal"
                severity="danger"
                rounded
                outlined
                class="icon-button"
                style="color:#E11D48; background: none"
                @click="onDeleteFile(slotProps.data)"
              >
                <!-- @todo Why is the above background 'none' necessary? -->
                <i class="pi pi-trash" />
              </Button>
            </template>
          </Column>
        </DataTable>
      </div>
      <div v-show="data.length === 0" class="text-base text-center font-medium">
        No Files
      </div>
      <div v-if="!isLoading" class="pt-2">
        <FileUpload
          :url="url"
          :accept="options.extensions"
          :max-file-size="options.fileSizeLimit"
          mode="basic"
          choose-label="Add File"
          :auto="true"
          :invalid-file-size-message="'{0} Invalid file size, file size should be smaller than ' + fileSizeInMB + ' MB'"
          :multiple="true"
          :choose-button-props="{size: 'small'}"
          custom-upload
          @uploader="onUpload(props.id, $event)"
        />
      </div>
    </div>
  </Section>
  <ConfirmDialog group="removeFile" />
</template>

<style>
.truncate {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    max-width: 150px;
}

.custom-row {
  background-color: transparent;
}
</style>
